import {HttpClient} from "@angular/common/http";
import {Inject, Injectable} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import {ITableState, TableResponseModel} from "../../../services/crud-table/models/table.model";
import {TableService} from "../../../services/crud-table/table.service";
import {PaginatorState} from "../../../services/crud-table/models/paginator.model";
import {GroupingState} from "../../../services/crud-table/models/grouping.model";
import {ROUTES} from "../../../constants";
import {UtilityService} from "../../../services/utility.service";
import { SupplierTenderModel } from "../models/supplier-tender.model";
import { BaseService } from "src/app/services/base.service";
import { BreadcrumbService } from "../../../services/breadcrumb.service";
import { TranslateService } from "@ngx-translate/core";

const DEFAULT_STATE: ITableState = {
    filter: {},
    paginator: new PaginatorState(),
    sorting: {
        column: 'deadLineDate',
        direction: 'desc',
    },
    searchTerm: "",
    grouping: new GroupingState()
};

@Injectable({
    providedIn: "root"
})

export class SupplierTenderService<T> extends BaseService<T> {

	currentLanguage: string;

    constructor(
        private utilityService: UtilityService,
		private translateService: TranslateService,
		private breadcrumbService: BreadcrumbService,
        @Inject(HttpClient) http
    ) {
        super(http);
        this.tableState = {
            filter: {},
            paginator: new PaginatorState(),
            sorting: {
                column: 'deadLineDate',
                direction: 'desc',
            },
            searchTerm: "",
            grouping: new GroupingState()
        };

		this.currentLanguage = this.translateService.currentLang;
		this.translateService.onLangChange.subscribe(response => {
			this.currentLanguage = response.lang;
		});

		this.tableState.paginator.pageSize = 10;
    }

    private _total$: BehaviorSubject<number> = new BehaviorSubject(0);

    get total$() {
        return this._total$.asObservable();
    }

    private _buyersFromTenders$: BehaviorSubject<any> = new BehaviorSubject([]);

    get buyersFromTenders$() {
        return this._buyersFromTenders$.asObservable();
    }

    private _tender$ = new BehaviorSubject({});

    get tender$() {
        return this._tender$.asObservable();
    }

    set tender$(value: any) {
        this._tender$.next(value);
    }

    private _tenders$: BehaviorSubject<T[]> = new BehaviorSubject([]);

    get tenders$() {
        return this._tenders$.asObservable();
    }

    private _categories$: BehaviorSubject<any> = new BehaviorSubject([]);

    get categories$() {
        return this._categories$.asObservable();
    }

    resetDefaultState(): void {
        this.findTenders(DEFAULT_STATE);
    }

    /**
     * Find Tenders by tableState. TableState is a filter for working with lists.
     * @param tableState :table state parameters
     * @returns
     */
    findTenders(tableState?: Partial<ITableState>): void {
        if(tableState) {
            tableState.paginator.pageSize = 10;
        }

        const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.SEARCH;
        const state = tableState ? tableState : this.tableState;
        this.tableState = state;
        this.post(url, state).subscribe((res: TableResponseModel<T>) => {
            this._tenders$.next(res.items);
            this._total$.next(res.total);
            // this.getBuyerOrganizations(res.items);
        });

		this.resetTableState();

    }

    /**
     * Get Tender Details by Id
     * @param id :string Id of tender
     * @returns
     */
    getTender(tenderId: string): void {
        const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.SEARCH + '/' + tenderId;
        this.getById(url, tenderId).subscribe((res: SupplierTenderModel) => {
			this.breadcrumbService.setBreadcrumbId(res['tenderId']);
            this._tender$.next(res);
        });
    }

    /**
     * Get all categories
     */
    fetchCategories(): Observable<any> {
        const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.CATEGORY.SEARCH;
        return this.post(url, {paginator: {}, searchTerm: ""}, this.currentLanguage);
    }

    /**
     * Get all buyer list
     */
    getBuyerList(): void {
        const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.BUYERS_LIST;
        this.http.get(url).subscribe(buyers => {
            this._buyersFromTenders$.next(buyers);
        })
    }

    /**
     * Get all category list
     */
    getCategoryList(): void {
        const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.CATEGORY_LIST;
        this.http.get(url).subscribe((categories: any) => {
            this._categories$.next(categories.items);
        })
    }

    /**
     * Get buyer organisation from tenders as unique values for e.g. filters
     */
    getBuyerOrganizations(tenders): void {
        const uniqueBuyer = [];

        tenders.map((tender) => {
            const index = uniqueBuyer.findIndex((buyer) => buyer?.value === tender?.buyerOrganizationId);
            index === -1
                ? uniqueBuyer.push({
                    label: tender?.buyer?.organizationName,
                    value: tender?.buyerOrganizationId,
                })
                : "";
        });
        this._buyersFromTenders$.next(uniqueBuyer);
    }

    public getDetailsRoute(): string {
        return ROUTES.SUPPLIER_ROUTE + ROUTES.SUPPLIER_TENDERS_ROUTE + ROUTES.TENDER_PRODUCT_DETAILS_ROUTE;
    }

    /**
     * Handle supplier tender details page redirection
     */
    public navigationToTendersDetailsPage(tenderId: string): void {
        this.utilityService.navigate(ROUTES.SUPPLIER_ROUTE + ROUTES.SUPPLIER_TENDERS_ROUTE + ROUTES.TENDER_PRODUCT_DETAILS_ROUTE +
            '/' + tenderId,
            {queryParams: {tab: "products"}},
        );
    }

	get _tableState() {
		return this.tableState;
	};

	resetTableState() {
		this.tableState = DEFAULT_STATE;
		this.tableState.paginator.pageSize = 10;
	}

	tenderSubscription(tenderId, updateParams) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.SEARCH + '/' + tenderId
			+ this.API_ROUTES.SUBSCRIPTIONS.SUBSCRIPTION;
        return this.put(url, updateParams);
	}
    tenderParticipation(tenderId, updateParams) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.SEARCH + '/' + tenderId
			+ this.API_ROUTES.SUBSCRIPTIONS.PARTICIPATION;
        return this.put(url, updateParams);
	}

	/**
	 * Create Quotation
	 */
	createQuotation(data): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.QUOTATION.CREATE;
		return this.create(data, url);
	}

	/**
	 * Edit Quotation
	 */
	editQuotation(id: string, data): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.QUOTATION.EDIT + id;
		return this.put(url, data);
	}


	/**
	 *	Find by search string
	 * @param tableState :table state parameters
	 * @returns
	 */
	findSupplierTender(tableState: ITableState) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS_PRODUCTS.SEARCH;
		return this.fetch(url);
	}

    downloadAllProductAttachments(tenderId: string, productId: string) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.SEARCH  + `/${tenderId}/product/${productId}/attachments/all`;
		return this.downloadItems(url, null, this.currentLanguage);
	}

    downloadProductAttachments(tenderId: string, productId: string, fileName: string) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.SEARCH + `/${tenderId}/product/${productId}/attachments?file=${fileName}`;
		return this.downloadItems(url, null, this.currentLanguage);
	}
	downloadProductAttachmentsZip(tenderId: string,) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.DOWNLOAD_ATTACHMENT + `/${tenderId}/product/export/all`;
		return this.downloadItems(url, null, this.currentLanguage);
	}
    /**
	 * saving event when supplier starts quotation process
	 */
    markQuotationProcessStarted(payload){
        const url=this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.QUOTATION.NEW_QUOTATION;
        return this.http.post(url,payload);
    }

    /**
     * download Additional Information Attachment File
     */
    downloadAddInfoAttachments(tenderId: string, fileName: string): Observable<Blob> {
        const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.TENDERS.BASE_URL
			+ `/${tenderId}/add-info/attachments?file=${fileName}`;
        return this.downloadItems(url);
    }
}
